import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EmployerCommerceShell from '../../components/employer/EmployerCommerceShell';
import { Badge, Button, Card, CardBody, CardHeader } from '../../components/ui/primitives';
import { fetchCommerceOrderStatus, type CommerceOrderStatus, useApi } from '../../services/api';
import { clearRememberedPendingCommerceOrder, getRememberedPendingCommerceOrder } from '../../services/payfast';

type EmployerCommerceOrderStatusPageProps = {
  variant?: 'status' | 'return' | 'cancel';
};

const PENDING_STATUS_POLL_INTERVAL_MS = 3000;
const PENDING_STATUS_POLL_TIMEOUT_MS = 60000;

const getErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const formatDateTime = (value: string | null) => {
  if (!value) return 'Not available yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available yet';
  return parsed.toLocaleString();
};

const getPaymentTone = (paymentStatus: CommerceOrderStatus['paymentStatus']) => {
  if (paymentStatus === 'PAID') return 'brand';
  if (paymentStatus === 'FAILED' || paymentStatus === 'CANCELLED') return 'danger';
  return 'copper';
};

const getPaymentLabel = (paymentStatus: CommerceOrderStatus['paymentStatus']) => {
  switch (paymentStatus) {
    case 'PENDING':
      return 'Payment pending';
    case 'INVOICE_REQUESTED':
      return 'Invoice requested';
    case 'INVOICED':
      return 'Invoice sent';
    case 'PAID':
      return 'Payment received';
    case 'FAILED':
      return 'Payment failed';
    case 'CANCELLED':
      return 'Payment cancelled';
    default:
      return paymentStatus;
  }
};

const getFulfilmentLabel = (status: CommerceOrderStatus['fulfillmentStatus']) => {
  switch (status) {
    case 'AWAITING_BILLING':
      return 'Awaiting invoice details';
    case 'AWAITING_PAYMENT':
      return 'Awaiting payment';
    case 'AWAITING_SCOPING':
      return 'Allocated for build';
    case 'FULFILLED':
      return 'Credits added to your account';
    case 'IN_BUILD':
      return 'Build in progress';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return 'In progress';
  }
};

const getSummaryMessage = (status: CommerceOrderStatus) => {
  if (status.fulfillmentStatus === 'FULFILLED' && status.companyCreditBalance) {
    return `Credits added to your account. Available balance: ${status.companyCreditBalance.availableBalance}.`;
  }
  if (status.fulfillmentStatus === 'AWAITING_SCOPING') {
    return 'Payment received. Your simulation has been allocated for build.';
  }
  if (status.paymentStatus === 'INVOICE_REQUESTED') {
    return 'Your invoice request has been received.';
  }
  if (status.paymentStatus === 'INVOICED') {
    return 'Your invoice has been sent and is waiting for payment.';
  }
  if (status.paymentStatus === 'PENDING') {
    return 'Your payment is still pending confirmation.';
  }
  if (status.paymentStatus === 'FAILED') {
    return 'This payment did not complete successfully.';
  }
  if (status.paymentStatus === 'CANCELLED') {
    return 'This payment was cancelled before completion.';
  }
  return 'Your order is being processed.';
};

const isFinalPaymentStatus = (paymentStatus: CommerceOrderStatus['paymentStatus']) =>
  paymentStatus === 'PAID' || paymentStatus === 'FAILED' || paymentStatus === 'CANCELLED';

const getVariantCopy = (variant: EmployerCommerceOrderStatusPageProps['variant']) => {
  if (variant === 'return') {
    return {
      title: 'Payment return',
      description: 'We checked your order after the payment return. This page shows the current backend status.',
    };
  }
  if (variant === 'cancel') {
    return {
      title: 'Payment cancelled',
      description: 'This page shows the current order state after a cancelled payment attempt.',
    };
  }
  return {
    title: 'Order status',
    description: 'Track the current payment and fulfilment status for this order.',
  };
};

const EmployerCommerceOrderStatusPage: React.FC<EmployerCommerceOrderStatusPageProps> = ({ variant = 'status' }) => {
  const api = useApi();
  const navigate = useNavigate();
  const { orderId: routeOrderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const [status, setStatus] = useState<CommerceOrderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const resolvedOrderId = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return routeOrderId || searchParams.get('orderId') || getRememberedPendingCommerceOrder() || null;
  }, [location.search, routeOrderId]);

  useEffect(() => {
    let mounted = true;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    const pollStartedAt = Date.now();

    const clearPollTimer = () => {
      if (pollTimer) {
        window.clearTimeout(pollTimer);
        pollTimer = null;
      }
    };

    const schedulePoll = () => {
      if (variant === 'cancel' || Date.now() - pollStartedAt >= PENDING_STATUS_POLL_TIMEOUT_MS) {
        return;
      }
      clearPollTimer();
      pollTimer = window.setTimeout(() => {
        void load(true);
      }, PENDING_STATUS_POLL_INTERVAL_MS);
    };

    const load = async (isPoll = false) => {
      if (!resolvedOrderId) {
        setLoading(false);
        setError('No order reference was found for this page.');
        return;
      }

      if (!isPoll) {
        setLoading(true);
      }
      setError(null);

      try {
        const nextStatus = await fetchCommerceOrderStatus(resolvedOrderId, api);
        if (!mounted) return;
        setStatus(nextStatus);
        if (isFinalPaymentStatus(nextStatus.paymentStatus)) {
          clearRememberedPendingCommerceOrder(resolvedOrderId);
          clearPollTimer();
        } else if (nextStatus.paymentStatus === 'PENDING') {
          schedulePoll();
        }
      } catch (loadError) {
        if (!mounted) return;
        setError(getErrorMessage(loadError, 'Unable to load order status right now.'));
      } finally {
        if (mounted && !isPoll) setLoading(false);
      }
    };

    void load();
    return () => {
      mounted = false;
      clearPollTimer();
    };
  }, [api, resolvedOrderId, variant]);

  const copy = getVariantCopy(variant);

  return (
    <EmployerCommerceShell
      title={copy.title}
      description={copy.description}
      actions={
        <>
          <Button variant="secondary" onClick={() => navigate('/employer/billing')}>
            Billing
          </Button>
          <Button variant="secondary" onClick={() => navigate('/employer/build-request')}>
            Build request
          </Button>
        </>
      }
    >
      {!resolvedOrderId ? (
        <Card className="border-[#fde68a] bg-[#fffbeb]">
          <p className="text-[14px] font-medium text-[#92400e]">
            No order reference was available for this page.
          </p>
        </Card>
      ) : null}

      {error ? (
        <Card className="border-[#fecaca] bg-[#fff5f5]">
          <p className="text-[14px] font-medium text-[#b91c1c]">{error}</p>
        </Card>
      ) : null}

      {loading ? (
        <Card className="border-[#dbe4e1]">
          <p className="text-[14px] text-[#64748b]">Loading order status...</p>
        </Card>
      ) : null}

      {status ? (
        <>
          <Card className="border-[#dbe4e1]">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Order</p>
                  <h2 className="mt-2 font-display text-[26px] font-bold text-[#0f172a]">{status.offeringName}</h2>
                  <p className="mt-1 break-all font-mono text-[13px] text-[#64748b]">{status.orderId}</p>
                </div>
                <Badge variant={getPaymentTone(status.paymentStatus)}>{getPaymentLabel(status.paymentStatus)}</Badge>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <p className="text-[15px] font-medium text-[#0f172a]">{getSummaryMessage(status)}</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-[14px] border border-[#e2e8f0] bg-[#fcfffe] p-4">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Status</p>
                  <p className="mt-2 text-[16px] font-semibold text-[#0f172a]">{getFulfilmentLabel(status.fulfillmentStatus)}</p>
                </div>
                <div className="rounded-[14px] border border-[#e2e8f0] bg-[#fcfffe] p-4">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Paid at</p>
                  <p className="mt-2 text-[16px] font-semibold text-[#0f172a]">{formatDateTime(status.paidAt)}</p>
                </div>
                <div className="rounded-[14px] border border-[#e2e8f0] bg-[#fcfffe] p-4">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[#64748b]">Available credits</p>
                  <p className="mt-2 text-[16px] font-semibold text-[#0f172a]">
                    {status.companyCreditBalance ? status.companyCreditBalance.availableBalance : 'Not applicable'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          <Card className="border-[#dbe4e1]">
            <CardHeader>
              <h3 className="font-display text-[22px] font-bold text-[#0f172a]">Next step</h3>
            </CardHeader>
            <CardBody className="space-y-3 text-[14px] text-[#475569]">
              {status.paymentStatus === 'PENDING' ? (
                <p>Give the payment a moment to settle, then refresh this page if needed.</p>
              ) : null}
              {status.paymentStatus === 'INVOICE_REQUESTED' ? (
                <p>Your invoice request is in progress. We will send the invoice using the saved invoice details.</p>
              ) : null}
              {status.paymentStatus === 'INVOICED' ? (
                <p>The invoice has been issued. Once payment is received, the order will move forward automatically.</p>
              ) : null}
              {status.paymentStatus === 'PAID' && status.fulfillmentStatus === 'FULFILLED' ? (
                <p>Your credits are ready to use in the platform.</p>
              ) : null}
              {status.paymentStatus === 'PAID' && status.fulfillmentStatus === 'AWAITING_SCOPING' ? (
                <p>Your simulation is now in our build queue. We will complete the build and move it through final checks before deployment to your dashboard.</p>
              ) : null}
              {status.paymentStatus === 'FAILED' || status.paymentStatus === 'CANCELLED' ? (
                <p>You can start a new payment or request an invoice from the commerce pages.</p>
              ) : null}

              <div className="flex flex-wrap gap-3 pt-2">
                <Button onClick={() => navigate('/employer/billing')}>Top up credits</Button>
                <Button variant="secondary" onClick={() => navigate('/employer/build-request')}>
                  New build request
                </Button>
              </div>
            </CardBody>
          </Card>
        </>
      ) : null}
    </EmployerCommerceShell>
  );
};

export default EmployerCommerceOrderStatusPage;
